import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import {
  type ActionItemCode,
  type ActionItemPriority,
  type ActionItemRow,
  type ActionItemSeverity,
  type RASCISnapshot
} from "../lib/actionItems.js";

/**
 * Manual Create Action Item (ADR-0053).
 * Spec: governance-foundation §19.3.
 *
 * Route: `/app/action-items/new` (dedicated route — not a modal, per UI_GUIDELINES
 * §1.3 which forbids modals with 5+ fields).
 *
 * Accessible to TenantBuilder + TenantAdmin (the create route enforces this
 * server-side; the nav button + route guard in TenantApp.tsx enforce it
 * client-side).
 */

const SEVERITIES: ActionItemSeverity[] = ["critical", "high", "medium", "low"];
const PRIORITIES: ActionItemPriority[] = ["critical", "high", "medium", "low"];

type FormState = {
  title: string;
  description: string;
  severity: ActionItemSeverity;
  priority: ActionItemPriority;
  item_type_code_id: string;
  due_at: string; // ISO date (yyyy-mm-dd) or empty
  responsible_ids: string[]; // mix of users, contacts, groups
  accountable_id: string | null;
  support_ids: string[];
  consulted_ids: string[];
  informed_ids: string[];
  assignee_user_id: string | null; // optional override
};

const EMPTY_FORM: FormState = {
  title: "",
  description: "",
  severity: "medium",
  priority: "medium",
  item_type_code_id: "",
  due_at: "",
  responsible_ids: [],
  accountable_id: null,
  support_ids: [],
  consulted_ids: [],
  informed_ids: [],
  assignee_user_id: null
};

function splitByKind(ids: string[], candidates: MemberCandidate[]): { userIds: string[]; groupIds: string[] } {
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const userIds: string[] = [];
  const groupIds: string[] = [];
  for (const id of ids) {
    const c = byId.get(id);
    if (!c) continue;
    if (c.kind === "group") groupIds.push(id);
    else userIds.push(id); // user or contact — both go into *_user_ids
  }
  return { userIds, groupIds };
}

/** ADR-0221 §3.6: what the issue page's "+ New action item" link carries in location state. */
export type FromIssueState = { source_ref?: { initiative_id?: string; initiative_issue_id?: string }; title?: string; initiative_name?: string };

/** The issue an action item is being created from, read off location state — or null. Pure, for the tests. */
export function fromIssue(state: unknown): { source_ref: { initiative_id: string; initiative_issue_id: string }; title: string; initiative_name: string | null } | null {
  const s = state as FromIssueState | null | undefined;
  const ref = s?.source_ref;
  if (!ref || typeof ref.initiative_issue_id !== "string" || typeof ref.initiative_id !== "string") return null;
  return { source_ref: { initiative_id: ref.initiative_id, initiative_issue_id: ref.initiative_issue_id }, title: typeof s?.title === "string" ? s.title : "", initiative_name: typeof s?.initiative_name === "string" ? s.initiative_name : null };
}

export function ActionItemCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  // ADR-0221 §3.6: created from an issue — the issue rides in source_ref; the title is a starting point.
  const issue = useMemo(() => fromIssue(location.state), [location.state]);

  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [itemTypes, setItemTypes] = useState<ActionItemCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(() => (issue?.title ? { ...EMPTY_FORM, title: issue.title } : EMPTY_FORM));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [dir, it] = await Promise.all([
          apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates"),
          apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/item-types")
        ]);
        setCandidates(dir.candidates);
        setItemTypes(it.codes);
      } catch (e) {
        setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const userOnlyCandidates = useMemo(
    () => candidates.filter((c) => c.kind === "user"),
    [candidates]
  );

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) errs.title = "Title is required.";
    if (!form.description.trim()) errs.description = "Description is required.";
    if (!form.item_type_code_id) errs.item_type_code_id = "Item type is required.";
    if (form.responsible_ids.length === 0) errs.responsible_ids = "At least one Responsible is required.";
    if (!form.accountable_id) errs.accountable_id = "Accountable is required.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function submit() {
    setSubmitError(null);
    if (!validate()) return;
    if (busy) return;
    setBusy(true);
    try {
      const resp = splitByKind(form.responsible_ids, candidates);
      const support = splitByKind(form.support_ids, candidates);
      const consulted = splitByKind(form.consulted_ids, candidates);
      const informed = splitByKind(form.informed_ids, candidates);
      const rasci: RASCISnapshot = {
        responsible_user_ids: resp.userIds,
        responsible_group_ids: resp.groupIds,
        accountable_user_id: form.accountable_id!,
        support_user_ids: support.userIds,
        support_group_ids: support.groupIds,
        consulted_user_ids: consulted.userIds,
        consulted_group_ids: consulted.groupIds,
        informed_user_ids: informed.userIds,
        informed_group_ids: informed.groupIds
      };
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        severity: form.severity,
        priority: form.priority,
        item_type_code_id: form.item_type_code_id,
        due_at: form.due_at ? new Date(`${form.due_at}T00:00:00`).toISOString() : null,
        assignee_user_id: form.assignee_user_id,
        rasci,
        ...(issue ? { source_ref: issue.source_ref } : {})
      };
      const res = await apiFetch<{ item: ActionItemRow }>("/api/tenant/action-items", {
        method: "POST",
        body
      });
      toast.push({ kind: "success", message: "Action item created." });
      navigate(`/app/action-items/${res.item.id}`);
    } catch (e) {
      setSubmitError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageLayout title="New Action Item">
      <div style={{ display: "grid", gap: 12 }}>
        {error && <ErrorState detail={error} />}
        {loading && !error && <LoadingState />}

        {!loading && !error && (
          <>
            {issue && (
              <div className="muted" style={{ fontSize: 13 }}>
                From issue “<Link to={`/app/issues/${issue.source_ref.initiative_issue_id}`}>{issue.title || "an issue"}</Link>”{issue.initiative_name ? <> on <Link to={`/app/initiatives/${issue.source_ref.initiative_id}`}>{issue.initiative_name}</Link></> : null}
              </div>
            )}
            <div className="card" style={{ display: "grid", gap: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Core</div>

              <label className="field">
                <span className="label">Title *</span>
                <input
                  className="input"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  onBlur={() => {
                    if (!form.title.trim()) setFieldErrors((p) => ({ ...p, title: "Title is required." }));
                  }}
                  placeholder="Short description of the issue"
                />
                {fieldErrors.title && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.title}</div>}
              </label>

              <label className="field">
                <span className="label">Description *</span>
                <textarea
                  className="input"
                  rows={4}
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  onBlur={() => {
                    if (!form.description.trim()) setFieldErrors((p) => ({ ...p, description: "Description is required." }));
                  }}
                  placeholder="What's wrong, what needs to happen, and any context"
                />
                {fieldErrors.description && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.description}</div>}
              </label>

              <div className="inputRow" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <label className="field">
                  <span className="label">Severity *</span>
                  <select
                    className="select"
                    value={form.severity}
                    onChange={(e) => update("severity", e.target.value as ActionItemSeverity)}
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span className="label">Priority *</span>
                  <select
                    className="select"
                    value={form.priority}
                    onChange={(e) => update("priority", e.target.value as ActionItemPriority)}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span className="label">Due date</span>
                  <input
                    type="date"
                    className="input"
                    value={form.due_at}
                    onChange={(e) => update("due_at", e.target.value)}
                  />
                </label>
              </div>

              <label className="field">
                <span className="label">Item type *</span>
                <select
                  className="select"
                  value={form.item_type_code_id}
                  onChange={(e) => update("item_type_code_id", e.target.value)}
                >
                  <option value="">Select…</option>
                  {itemTypes.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                {fieldErrors.item_type_code_id && (
                  <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.item_type_code_id}</div>
                )}
                {itemTypes.length === 0 && (
                  <div className="help">
                    No item types defined for this tenant. <Link to="/app/admin/action-item-codes">Configure codes</Link>.
                  </div>
                )}
              </label>
            </div>

            <div className="card" style={{ display: "grid", gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700 }}>RASCI</div>
                <div className="help" style={{ marginTop: 4 }}>
                  Who owns, does, helps with, and hears about this item. Contacts receive email only; groups expand to their active members at creation time.
                </div>
              </div>

              <div className="field">
                <span className="label">Responsible *</span>
                <div className="help" style={{ marginBottom: 6 }}>
                  The people who <strong>do the work</strong>. Multiple allowed. Users, contacts, and groups all welcome. At least one is required.
                </div>
                <MemberPicker
                  mode="multi"
                  allowedKinds={["user", "contact", "group"]}
                  candidates={candidates}
                  selected={form.responsible_ids}
                  onChange={(next) => update("responsible_ids", next)}
                  placeholder="Add responsible parties…"
                  errorMessage={fieldErrors.responsible_ids}
                />
              </div>

              <div className="field">
                <span className="label">Accountable *</span>
                <div className="help" style={{ marginBottom: 6 }}>
                  The <strong>single person who answers for the outcome</strong>. Must be a platform user (not a contact, not a group) — they need to sign off on closure.
                </div>
                <MemberPicker
                  mode="single"
                  allowedKinds={["user"]}
                  candidates={candidates}
                  selected={form.accountable_id ? [form.accountable_id] : []}
                  onChange={(next) => update("accountable_id", next[0] ?? null)}
                  placeholder="Select accountable user…"
                  errorMessage={fieldErrors.accountable_id}
                />
              </div>

              <div className="field">
                <span className="label">Support</span>
                <div className="help" style={{ marginBottom: 6 }}>
                  People who <strong>help Responsible get it done</strong>. Resources, collaborators, specialists. Optional.
                </div>
                <MemberPicker
                  mode="multi"
                  allowedKinds={["user", "contact", "group"]}
                  candidates={candidates}
                  selected={form.support_ids}
                  onChange={(next) => update("support_ids", next)}
                  placeholder="Add support…"
                />
              </div>

              <div className="field">
                <span className="label">Consulted</span>
                <div className="help" style={{ marginBottom: 6 }}>
                  People whose <strong>input is needed before work proceeds or closes</strong>. Two-way — they give feedback and hear back. Optional.
                </div>
                <MemberPicker
                  mode="multi"
                  allowedKinds={["user", "contact", "group"]}
                  candidates={candidates}
                  selected={form.consulted_ids}
                  onChange={(next) => update("consulted_ids", next)}
                  placeholder="Add consulted…"
                />
              </div>

              <div className="field">
                <span className="label">Informed</span>
                <div className="help" style={{ marginBottom: 6 }}>
                  People who just need to <strong>know the outcome</strong>. One-way — they receive updates but aren't expected to act. Optional.
                </div>
                <MemberPicker
                  mode="multi"
                  allowedKinds={["user", "contact", "group"]}
                  candidates={candidates}
                  selected={form.informed_ids}
                  onChange={(next) => update("informed_ids", next)}
                  placeholder="Add informed…"
                />
              </div>

              <div className="field">
                <span className="label">Assignee (optional)</span>
                <div className="help" style={{ marginBottom: 6 }}>
                  Who this item sits with right now. Defaults to the first Responsible user on save. You can reassign later.
                </div>
                <MemberPicker
                  mode="single"
                  allowedKinds={["user"]}
                  candidates={userOnlyCandidates}
                  selected={form.assignee_user_id ? [form.assignee_user_id] : []}
                  onChange={(next) => update("assignee_user_id", next[0] ?? null)}
                  placeholder="Defaults to first Responsible user"
                />
              </div>
            </div>

            {submitError && <ErrorState detail={submitError} />}

            <div className="actions">
              <Link className="btn" to="/app/action-items">Cancel</Link>
              <button
                type="button"
                className="btn btnPrimary"
                disabled={busy}
                onClick={() => void submit()}
              >
                {busy ? "Creating…" : "Create action item"}
              </button>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
